import * as React from 'react';

import AppBar from '@material-ui/core/AppBar';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import BasicDataPanel from "../containers/BasicDataPanel";
import Canvas from '../containers/Canvas';
import GenericDialog from '../containers/GenericDialog';
import HelpPanel from '../containers/HelpPanel';
import MainToolbar from '../containers/MainToolbar';
import NoteDialog from '../containers/NoteDialog';
import ProgressStepper from '../containers/ProgressStepper';
import SlideNotesDialog from '../containers/SlideNotesDialog';
import Storyboard from "../containers/Storyboard";
import TimeDialog from '../containers/TimeDialog';

import { ProcessStep } from '../constants';
import TextShapeDialog from "../containers/TextShapeDialog";
import rafSchedule from "raf-schd";


interface Props {
    readonly processStep: ProcessStep,
    readonly captureMouse: boolean,
    readonly onRegularKeyPressed?: () => void,
    readonly onMouseMove: (x: number, y: number) => void,
    readonly onMouseUp: (x: number, y: number) => void,
}

const styles = (theme: Theme) => createStyles({
    canvas: {
        flex: 1,
        overflow: 'hidden',
    },
    scrollableCanvas: {
        flex: 1,
        overflow: 'auto',
    },
    main: {
        flex: 1,
    },
    root: {
        alignItems: 'stretch',
        backgroundColor: theme.palette.background.default,
        width: '100%',
        height: '100vh',
    },
    sidebar: {
        flex: 0,
        borderRight: `solid thin ${theme.palette.divider}`,
    },
    sidebarContent: {
        flex: 1,
    },
    mainToolbar: {
        flex: 0,
    },
    toolbar: {
        minHeight: 67,
        borderBottom: `solid thin ${theme.palette.divider}`,
    }
});

type Styles =
    'root'
    | 'sidebar'
    | 'sidebarContent'
    | 'main'
    | 'mainToolbar'
    | 'canvas'
    | 'scrollableCanvas'
    | 'toolbar';

class MainLayout extends React.Component<Props & WithStyles<Styles>> {

    private readonly scheduleMouseMove: ((x: number, y: number) => void) & { cancel: () => void } =
        rafSchedule(
            (x: number, y: number) =>
                this.props.onMouseMove(x, y)
        );

    public render() {
        const {processStep, captureMouse, classes, onRegularKeyPressed} = this.props;
        return (
            <div className={`${classes.root} hbox`}
                 tabIndex={-1}
                 onKeyDownCapture={(e) => {
                     if (onRegularKeyPressed && e && e.key.match(/^\w$/)) {
                         onRegularKeyPressed();
                     }
                 }}
                 onMouseUp={captureMouse? this.onMouseUp : undefined}
                 onMouseMove={captureMouse? this.handleMouseMove : undefined}
            >
                <div className={`${classes.sidebar} vbox`}>
                    <AppBar color="primary" position="static" elevation={0} className={classes.toolbar}>
                        <Toolbar>
                            <Typography variant="h6" color="inherit">
                                PresoBoard
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <Paper elevation={0} className={classes.sidebarContent}>
                        <ProgressStepper/>
                    </Paper>
                </div>
                <div className={`${classes.main} vbox`}>
                    <div className={classes.mainToolbar}>
                        <MainToolbar/>
                    </div>
                    <div className={processStep !== ProcessStep.BASIC_DATA ? classes.canvas : classes.scrollableCanvas}>
                        {(() => {
                            switch (processStep) {
                                case ProcessStep.BASIC_DATA:
                                    return <BasicDataPanel/>;
                                case ProcessStep.SKETCH:
                                    return <Storyboard/>;
                                default:
                                    return <Canvas/>;
                            }
                        })()}
                    </div>
                </div>
                <HelpPanel/>

                <NoteDialog/>
                <TextShapeDialog/>
                <SlideNotesDialog/>
                <TimeDialog/>
                <GenericDialog/>
            </div>
        );
    }

    private readonly handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
        this.scheduleMouseMove(e.clientX / window.innerWidth, e.clientY / window.innerHeight);
    };

    private readonly onMouseUp = (e: React.MouseEvent<HTMLDivElement>): void => {
        this.props.onMouseUp(e.clientX / window.innerWidth, e.clientY / window.innerHeight);
    };

}

export default withStyles(styles)(MainLayout);


// WEBPACK FOOTER //
// ./src/components/MainLayout.tsx